import axios from 'axios';

/**
 * Read a cookie by name (used for the CSRF double-submit cookie).
 */
export function readCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// In-memory cache of the CSRF token. Required when the SPA and API live on
// different origins in dev (e.g. localhost:5173 → localhost:3000), because
// document.cookie cannot read a cookie set by a different origin. The API
// also returns the token in the X-CSRF-Token response header, which CORS
// exposes — we capture it here.
let csrfTokenCache = null;

// Capture the CSRF token from any API response header.
api.interceptors.response.use(
  (r) => {
    const header = r.headers?.['x-csrf-token'];
    if (header) csrfTokenCache = header;
    return r;
  },
  (err) => {
    const header = err.response?.headers?.['x-csrf-token'];
    if (header) csrfTokenCache = header;
    const data = err.response?.data;
    if (data?.error) {
      const e = new Error(data.error.message || 'Request failed');
      e.code = data.error.code;
      e.details = data.error.details;
      e.status = err.response?.status;
      return Promise.reject(e);
    }
    return Promise.reject(err);
  },
);

async function ensureCsrfToken() {
  if (csrfTokenCache) return csrfTokenCache;
  const fromCookie = readCookie('eprx.csrf');
  if (fromCookie) {
    csrfTokenCache = fromCookie;
    return csrfTokenCache;
  }
  // Prime the token via a safe request — the response will set the header.
  try {
    await api.get('/auth/me');
  } catch {
    // 401 is expected when not logged in; we still get the CSRF header.
  }
  return csrfTokenCache;
}

// Inject CSRF token on mutating requests.
api.interceptors.request.use(async (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (!['get', 'head', 'options'].includes(method)) {
    const token = csrfTokenCache || readCookie('eprx.csrf') || (await ensureCsrfToken());
    if (token) {
      config.headers['X-CSRF-Token'] = token;
    }
  }
  return config;
});
